import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Slot } from "radix-ui";
import { cn } from "@/lib/cn";
import { Spinner } from "./spinner";

const variants = {
  primary:
    "bg-primary text-white shadow-button hover:bg-primary-hover active:translate-y-px disabled:bg-primary/60 disabled:shadow-none",
  secondary: "bg-white text-slate-900 ring-1 ring-inset ring-slate-900/10 hover:bg-slate-50 hover:ring-slate-900/15 disabled:text-slate-400",
  soft: "bg-primary-soft text-brand-700 hover:bg-brand-200/70 disabled:opacity-60",
  ghost: "text-slate-700 hover:bg-slate-900/5 disabled:text-slate-400",
  danger: "bg-danger text-white hover:bg-red-700 disabled:bg-danger/60",
  "danger-ghost": "text-danger hover:bg-danger-soft disabled:opacity-60",
  whatsapp: "bg-whatsapp text-white hover:bg-whatsapp-dark shadow-[0_10px_24px_-12px_rgb(37_211_102/0.8)]",
  link: "text-primary underline-offset-4 hover:underline px-0 h-auto",
} as const;

const sizes = {
  sm: "h-9 px-3.5 text-[13px] gap-1.5",
  md: "h-11 px-5 text-sm gap-2",
  lg: "h-12 px-6 text-[15px] gap-2",
  icon: "size-11 p-0",
  "icon-sm": "size-9 p-0",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
  asChild?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const buttonClass = (variant: keyof typeof variants = "primary", size: keyof typeof sizes = "md", className?: string) =>
  cn(
    "inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-pill font-semibold transition-[background-color,box-shadow,color,transform] duration-150 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary [&_svg]:size-4 [&_svg]:shrink-0",
    variants[variant],
    sizes[size],
    className,
  );

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, asChild = false, leftIcon, rightIcon, className, children, disabled, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";
  if (asChild) {
    return (
      <Comp ref={ref} className={buttonClass(variant, size, className)} {...props}>
        {children}
      </Comp>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={buttonClass(variant, size, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
